import { useCallback, useMemo } from 'react';
import { analysisSelector } from '../../redux/selectors/analyses';
import { useAppSelector } from '../../redux/hooks';
import {
  IFunnelStepBriefInfoFieldsFragment,
  IUserAliasReplacementFieldsFragment,
} from '../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../utils/types';
import { getAliasedName } from '../../utils/redux/applyAliasReplacements';
import { parseAliasReplacements } from '../../utils/redux/util';
import { SingleSelectMenu } from '../Dropdown';
import { ISingleSelectMenuOption } from '../Dropdown/SingleSelectMenu/types';

interface IProps {
  steps: IFunnelStepBriefInfoFieldsFragment[];
  selectedStep: string | null;
  onChange: (stepIndex: string) => void;
  aliasReplacements: IUserAliasReplacementFieldsFragment[];
  analysisId?: string;
  placeholder?: string;
  // else case is In Significance builder - funnel step menu
  isTimeToConvertCohortRow?: boolean;
}

const FunnelStepMenu = ({
  steps,
  selectedStep,
  onChange,
  aliasReplacements,
  analysisId,
  placeholder,
  isTimeToConvertCohortRow,
}: IProps) => {
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, analysisId),
  );
  // If applicable, we have to apply aliases to step names:
  // 1. First we will try to get aliasReplacements from chartData in case
  // if we opened Funnel report, renamed steps, and we are using same Funnel
  // in Builder
  // 2. Get aliasReplacements from chart pref
  const getStepName = useCallback(
    (step: IFunnelStepBriefInfoFieldsFragment) => {
      const { name, seq, userAlias } = step;
      assertNotNil(seq);

      const chartDataAliasReplacements =
        currentAnalysis?.chartData &&
        'aliasReplacements' in currentAnalysis?.chartData
          ? currentAnalysis?.chartData?.aliasReplacements
          : null;

      if (chartDataAliasReplacements) {
        return `${seq + 1}: ${
          getAliasedName(name, userAlias.aliasId, chartDataAliasReplacements)
            .name
        }`;
      } else {
        const parsedAliasReplacements =
          parseAliasReplacements(aliasReplacements);
        return `${seq + 1}: ${
          getAliasedName(name, userAlias.aliasId, parsedAliasReplacements).name
        }`;
      }
    },
    [currentAnalysis, aliasReplacements],
  );
  const options = useMemo(() => {
    if (isTimeToConvertCohortRow) {
      return steps.reduce((acc: ISingleSelectMenuOption[], step, index) => {
        // special case: if there are only 2 steps,
        // we only need to show first step -> last step, so skip on last index.
        if (steps.length === 2 && index === steps.length - 1) {
          return acc;
        }
        const { seq } = step;
        assertNotNil(seq);
        // for last option, if current step is the last step, we will show the first step -> last step
        const isLastStep = step.seq === steps.length - 1;
        const nextStep = isLastStep ? steps[index] : steps[index + 1];
        const startStep = isLastStep ? steps[0] : steps[index];

        acc.push({
          text: `${getStepName(startStep)} - ${getStepName(nextStep)}`,
          value: seq.toString(),
        });
        return acc;
      }, []);
    } else {
      return steps.map((step) => {
        const { seq } = step;
        assertNotNil(seq);
        return {
          text: getStepName(step),
          value: seq.toString(),
        };
      });
    }
  }, [steps, isTimeToConvertCohortRow, getStepName]);

  return (
    <SingleSelectMenu
      options={options}
      value={selectedStep}
      onChange={onChange}
      placeholder={placeholder}
      disabled={!options.length}
    />
  );
};

export default FunnelStepMenu;
